import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { CaretDown, Check, CheckCircle, Info, WarningCircle } from '@phosphor-icons/react';
import { SolarDatePicker } from '@airachat/solar-datepicker/react';

interface SmartInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label: string;
  hint: string;
  error?: string;
  /** Shown in place of the hint once the value passes — say what it IS, not that it is valid. */
  success?: string;
  wide?: boolean;
  /** Machine identifiers: tabular figures, so 11 and 14 digit codes stay countable. */
  code?: boolean;
  showValidation?: boolean;
  onValueChange: (value: string) => void;
}

export function SmartInput({
  id,
  label,
  hint,
  error = '',
  success = '',
  wide = false,
  code = false,
  required = false,
  showValidation = false,
  value,
  onValueChange,
  ...inputProps
}: SmartInputProps) {
  const hintId = `${id}-hint`;
  const visibleError = showValidation ? error : '';
  const valid = showValidation && !visibleError && String(value ?? '').trim().length > 0;
  const confirmation = valid ? success : '';

  return (
    <label className={`field smart-field${wide ? ' field--wide' : ''}${code ? ' smart-field--code' : ''}${visibleError ? ' smart-field--error' : ''}${valid ? ' smart-field--valid' : ''}`} htmlFor={id}>
      <span className="smart-field__label">{label}{required && <b aria-hidden="true">*</b>}</span>
      <span className="smart-field__control">
        <input
          // A `code` field holds a Latin-only, machine-read value: a username,
          // a mobile number, an account code. Mobile keyboards capitalise the
          // first letter of a text field and autocorrect the rest, which turns
          // `owner` into `Owner` and a valid credential into a rejected one.
          // Anything the caller passes still wins, via the spread below.
          {...(code
            ? { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }
            : {})}
          {...inputProps}
          id={id}
          required={required}
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          aria-invalid={visibleError ? true : undefined}
          aria-describedby={hintId}
        />
        {visibleError ? <WarningCircle className="smart-field__state" aria-hidden="true" /> : valid ? <CheckCircle className="smart-field__state" aria-hidden="true" /> : null}
      </span>
      <small
        id={hintId}
        className={`smart-field__message${confirmation ? ' smart-field__message--ok' : ''}`}
        role={visibleError ? 'alert' : undefined}
      >
        {visibleError ? <WarningCircle aria-hidden="true" /> : confirmation ? <CheckCircle aria-hidden="true" /> : <Info aria-hidden="true" />}
        <span>{visibleError || confirmation || hint}</span>
      </small>
    </label>
  );
}

/**
 * A date field that reads and writes Jalali. Fiscal years in this product are
 * Jalali — «۱ فروردین ۱۴۰۵», not «2026-03-21» — so that is what the field
 * shows and what the calendar counts in. The value crossing the wire stays
 * Gregorian ISO, because the server schema, the fiscal-period engine and every
 * stored date already speak it; converting at the edge keeps one calendar in
 * the database and the other in front of the person reading it.
 *
 * It also removes a hazard the old text input had: it saved on every keystroke,
 * so typing a date sent ten PATCHes, most of them half-written and rejected.
 * A picker emits one complete date, once.
 */
export function SmartDateField({
  id,
  label,
  hint,
  value,
  onChange,
  disabled = false,
  wide = false,
  inline = false,
  minIso,
  maxIso,
}: {
  id: string;
  label: string;
  hint: string;
  /** Gregorian ISO `YYYY-MM-DD`, or '' for empty. */
  value: string;
  onChange: (iso: string) => void;
  disabled?: boolean;
  wide?: boolean;
  /** Draw the calendar open in the page — for dialogs, whose own scroll box would clip a popover. */
  inline?: boolean;
  minIso?: string;
  maxIso?: string;
}) {
  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;

  return (
    // A group rather than a <label>: the control is a button that opens a
    // dialog, and a label wrapping it would fight the click.
    <div
      className={`field smart-field smart-date${wide ? ' field--wide' : ''}`}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={hintId}
    >
      <span className="smart-field__label" id={labelId}>{label}</span>
      <SolarDatePicker
        value={value}
        onChange={onChange}
        disabled={disabled}
        inline={inline}
        placeholder="انتخاب تاریخ"
        minIso={minIso}
        maxIso={maxIso}
      />
      <small id={hintId} className="smart-field__message">
        <Info aria-hidden="true" /><span>{hint}</span>
      </small>
    </div>
  );
}

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface MenuRect {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  opensUp: boolean;
}

export function CustomSelect<T extends string>({
  id,
  ariaLabel,
  value,
  onChange,
  options,
  className = '',
  disabled = false,
}: {
  id?: string;
  ariaLabel: string;
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  className?: string;
  disabled?: boolean;
}) {
  const generatedId = useId().replace(/:/g, '');
  const triggerId = id ?? `custom-select-${generatedId}`;
  const listboxId = `${triggerId}-listbox`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const typeahead = useRef('');
  const typeaheadTimer = useRef<number>();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [rect, setRect] = useState<MenuRect | null>(null);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = options[selectedIndex];

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const reposition = useCallback(() => {
    const trigger = triggerRef.current?.getBoundingClientRect();
    if (!trigger) return;
    const margin = 8;
    const width = Math.min(Math.max(trigger.width, 200), window.innerWidth - margin * 2);
    const left = Math.min(Math.max(trigger.right - width, margin), window.innerWidth - width - margin);
    const below = window.innerHeight - trigger.bottom;
    const opensUp = below < 230 && trigger.top > below;
    setRect({
      top: opensUp ? undefined : trigger.bottom + 5,
      bottom: opensUp ? window.innerHeight - trigger.top + 5 : undefined,
      left,
      width,
      opensUp,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) close();
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [close, open, reposition]);

  function nextEnabledIndex(start: number, direction: 1 | -1): number {
    if (!options.length) return -1;
    for (let offset = 1; offset <= options.length; offset += 1) {
      const index = (start + direction * offset + options.length) % options.length;
      if (!options[index]?.disabled) return index;
    }
    return -1;
  }

  function show() {
    setOpen(true);
    setActiveIndex(selectedIndex >= 0 && !options[selectedIndex]?.disabled ? selectedIndex : nextEnabledIndex(-1, 1));
  }

  function commit(index: number) {
    const option = options[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    close();
    requestAnimationFrame(() => triggerRef.current?.focus());
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      triggerRef.current?.focus();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        show();
      } else {
        setActiveIndex((current) => nextEnabledIndex(current, event.key === 'ArrowDown' ? 1 : -1));
      }
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      if (!open) show();
      setActiveIndex(event.key === 'Home' ? nextEnabledIndex(-1, 1) : nextEnabledIndex(0, -1));
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!open) show(); else commit(activeIndex);
      return;
    }
    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      typeahead.current += event.key.toLocaleLowerCase('fa');
      if (typeaheadTimer.current) window.clearTimeout(typeaheadTimer.current);
      typeaheadTimer.current = window.setTimeout(() => { typeahead.current = ''; }, 650);
      const match = options.findIndex((option) => !option.disabled && option.label.toLocaleLowerCase('fa').startsWith(typeahead.current));
      if (match >= 0) {
        event.preventDefault();
        if (!open) setOpen(true);
        setActiveIndex(match);
      }
    }
  }

  return (
    <div className={`custom-select ${open ? 'custom-select--open' : ''} ${className}`}>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        className="custom-select__trigger"
        role="combobox"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        disabled={disabled}
        onClick={() => open ? close() : show()}
        onKeyDown={handleKeyDown}
      >
        <span>{selected?.label ?? ariaLabel}</span>
        <CaretDown aria-hidden="true" />
      </button>
      {open && rect && createPortal(
        <div
          ref={menuRef}
          id={listboxId}
          role="listbox"
          aria-labelledby={triggerId}
          className={`custom-select__menu ${rect.opensUp ? 'custom-select__menu--up' : ''}`}
          style={{ top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width }}
          onKeyDown={handleKeyDown}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              id={`${listboxId}-option-${index}`}
              type="button"
              role="option"
              aria-selected={option.value === value}
              // `disabled` blocks the pointer, but a role="option" announces its
              // state through aria-disabled — without it the option reads as
              // available to anyone not using a mouse.
              aria-disabled={option.disabled || undefined}
              disabled={option.disabled}
              className={`custom-select__option${index === activeIndex ? ' custom-select__option--active' : ''}${option.value === value ? ' custom-select__option--selected' : ''}`}
              onPointerEnter={() => !option.disabled && setActiveIndex(index)}
              onClick={() => commit(index)}
            >
              <span><strong>{option.label}</strong>{option.description && <small>{option.description}</small>}</span>
              {option.value === value && <Check aria-hidden="true" />}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

export function SmartSelect<T extends string>({
  id,
  label,
  hint,
  value,
  onChange,
  options,
  wide = false,
}: {
  id: string;
  label: string;
  hint: string;
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  wide?: boolean;
}) {
  return (
    <div className={`field smart-field${wide ? ' field--wide' : ''}`}>
      <span className="smart-field__label">{label}</span>
      <CustomSelect id={id} ariaLabel={label} value={value} onChange={onChange} options={options} />
      <small className="smart-field__message"><Info aria-hidden="true" /><span>{hint}</span></small>
    </div>
  );
}
